import React from 'react';
import { useLocation } from 'react-router-dom';
import { User, NotebookPen, MessageCircle, QrCode, HeartPulse, Trash2 } from 'lucide-react';
import { pageTitleMap } from 'utils/navigation';
import { AppPage } from 'types';
import { Button } from 'components/ui/button';
import { SidebarTrigger } from 'components/ui/sidebar';
import { Separator } from 'components/ui/separator';
import { ThemeToggle } from 'components/ThemeToggle';

const FOR_YOU_TITLES: Record<string, string> = {
  'your-day': 'Your Day',
  saved: 'Saved Items',
  history: 'Your History',
};

interface HeaderProps {
  onOpenProfile?: () => void;
  activePage?: string;
  hasData?: boolean;
  onScrollToJournal?: () => void;
  onToggleJournalNotes?: () => void;
  onChatWithAI?: () => void;
  onFetchFrom1177?: () => void;
  isFetchingFrom1177?: boolean;
  onRunHealthAnalysis?: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  onOpenProfile,
  activePage,
  hasData,
  onScrollToJournal,
  onToggleJournalNotes,
  onChatWithAI,
  onFetchFrom1177,
  isFetchingFrom1177 = false,
  onRunHealthAnalysis,
}) => {
  const location = useLocation();
  const isEhrPage = activePage === 'ehr';

  return (
    <header className="flex h-12 shrink-0 items-center gap-2 border-b bg-background px-4">
      <SidebarTrigger className="-ml-1" />
      <Separator orientation="vertical" className="mr-2 h-4" />
      <span className="text-sm font-medium truncate">
        {activePage === 'for-you'
          ? (FOR_YOU_TITLES[location.pathname.split('/').pop() || ''] ?? pageTitleMap[activePage as AppPage] ?? '')
          : (pageTitleMap[activePage as AppPage] ?? '')}
      </span>

      <div className="flex flex-1 items-center justify-end gap-1.5">
        {isEhrPage && (
          <>
            {onScrollToJournal && (
              <Button variant="ghost" size="sm" onClick={onScrollToJournal}>
                <NotebookPen className="mr-1 h-4 w-4" />
                Journal
              </Button>
            )}
            {onChatWithAI && (
              <Button variant="ghost" size="sm" onClick={onChatWithAI}>
                <MessageCircle className="mr-1 h-4 w-4" />
                Chat
              </Button>
            )}
            {onFetchFrom1177 && (
              <Button variant="ghost" size="sm" onClick={onFetchFrom1177} disabled={isFetchingFrom1177}>
                <QrCode className="mr-1 h-4 w-4" />
                {isFetchingFrom1177 ? 'Loading...' : '1177'}
              </Button>
            )}
            {hasData && onRunHealthAnalysis && (
              <Button variant="ghost" size="sm" onClick={onRunHealthAnalysis}>
                <HeartPulse className="mr-1 h-4 w-4" />
                Analysis
              </Button>
            )}
          </>
        )}
        {activePage === 'eir-chat' && (
          <Button
            variant="ghost"
            size="icon"
            className="text-destructive hover:bg-destructive/10"
            onClick={() => {
              window.dispatchEvent(new CustomEvent('delete-current-chat-thread'));
            }}
            title="Delete chat"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
        <ThemeToggle />
        {onOpenProfile && (
          <Button variant="ghost" size="icon" onClick={onOpenProfile} title="Profile">
            <User className="h-4 w-4" />
          </Button>
        )}
      </div>
    </header>
  );
};
